import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableListField } from '@/components/form/TranslatableListField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { listToInput, valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import { type VacancyInput, type VacancyRow, VACANCY_TYPES } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/vacancies`.
 *
 * §4 of the data model publishes a competition's *results* as a document
 * against the original vacancy rather than as a separate announcement. So the
 * job this screen really does is not posting the competition — it is closing
 * it, and the list leads with the ones still owing a result.
 */
export function VacanciesPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams();

  const columns: Column<VacancyRow>[] = [
    {
      key: 'title',
      label: t('content.columns.title'),
      render: (row) => (
        <div className="min-w-48">
          <span className="font-semibold text-primary">{valueFor(row.title, locale)}</span>

          <span className="mt-1 flex flex-wrap gap-1.5">
            {/*
              The flag this module exists for. `is_closed` is computed by the
              server, in the university's timezone rather than the reader's.
            */}
            {row.is_closed && !row.is_result_published ? (
              <Chip tone="gold">{t('content.vacancies.awaitingResults')}</Chip>
            ) : null}
            {row.is_result_published ? (
              <Chip tone="navy">{t('content.vacancies.resultsPublished')}</Chip>
            ) : null}
          </span>
        </div>
      ),
    },
    {
      key: 'type',
      label: t('content.vacancies.columns.type'),
      render: (row) => (
        <span className="text-secondary">{t(`content.vacancies.types.${row.type}`)}</span>
      ),
    },
    {
      key: 'faculty',
      label: t('content.vacancies.columns.faculty'),
      render: (row) => (
        <span className="text-caption text-secondary">
          {row.faculty ? valueFor(row.faculty.name, locale) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'deadline',
      label: t('content.vacancies.columns.deadline'),
      sortable: true,
      align: 'end',
      render: (row) => (
        <time dateTime={row.deadline} className="whitespace-nowrap text-secondary">
          {new Date(row.deadline).toLocaleDateString()}
        </time>
      ),
    },
  ];

  return (
    <ResourceListScreen
      resource="vacancies"
      title={t('nav.items.vacancies')}
      intro={t('content.vacancies.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.title, locale)}
      emptyTitle={t('content.vacancies.empty.title')}
      emptyBody={t('content.vacancies.empty.body')}
      newLabel={t('content.vacancies.new')}
      deleteTitle={t('content.vacancies.delete.title')}
      deleteBody={(label) => t('content.delete.body', { label })}
      filters={
        <>
          <Select
            label={t('content.vacancies.columns.type')}
            hideLabel
            value={String(list.params.type ?? '')}
            onChange={(event) => list.setFilter('type', event.target.value || null)}
            options={[
              { value: '', label: t('content.vacancies.anyType') },
              ...VACANCY_TYPES.map((type) => ({
                value: type,
                label: t(`content.vacancies.types.${type}`),
              })),
            ]}
          />

          <Button
            type="button"
            size="sm"
            variant={list.params.awaiting_results ? 'primary' : 'secondary'}
            aria-pressed={Boolean(list.params.awaiting_results)}
            onClick={() =>
              list.setFilter('awaiting_results', list.params.awaiting_results ? null : '1')
            }
          >
            {t('content.vacancies.awaitingResults')}
          </Button>
        </>
      }
    />
  );
}

interface Fields {
  title: TranslatedValue;
  slug: TranslatedValue;
  unit: TranslatedValue;
  description: TranslatedValue;
  requirements: TranslatedValue;
  required_documents: Record<string, string[] | null>;
  application_instructions: TranslatedValue;
  type: (typeof VACANCY_TYPES)[number];
  faculty_id: string;
  deadline: string;
  result_document_id: string;
  is_result_published: boolean;
  status: VacancyRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayPublish = can('vacancies.publish');

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<VacancyRow, Fields, VacancyInput>({
      resource: 'vacancies',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'title',
        'slug',
        'unit',
        'description',
        'requirements',
        'application_instructions',
        'type',
        'faculty_id',
        'deadline',
        'result_document_id',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">
            {isNew ? t('content.vacancies.new') : t('content.vacancies.edit')}
          </h1>

          {record?.is_closed && !record.is_result_published ? (
            <Chip tone="gold" className="mt-2">
              {t('content.vacancies.awaitingResults')}
            </Chip>
          ) : null}
        </div>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/vacancies')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('content.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={formState.errors.title?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t('content.vacancies.columns.type')}
              {...register('type')}
              options={VACANCY_TYPES.map((type) => ({
                value: type,
                label: t(`content.vacancies.types.${type}`),
              }))}
            />

            <Input
              {...register('deadline')}
              type="date"
              label={t('content.vacancies.fields.deadline')}
              error={formState.errors.deadline?.message}
            />

            <Input
              {...register('faculty_id')}
              type="number"
              min={1}
              label={t('content.vacancies.fields.faculty')}
              hint={t('content.vacancies.fields.facultyHint')}
              error={formState.errors.faculty_id?.message}
            />
          </div>

          <TranslatableField
            label={t('content.vacancies.fields.unit')}
            hint={t('content.vacancies.fields.unitHint')}
            name="unit"
            value={values.unit}
            onChange={(next) => setValue('unit', next, { shouldDirty: true })}
            required={false}
          />

          <RichTextField
            label={t('content.fields.description')}
            value={values.description}
            onChange={(next) => setValue('description', next, { shouldDirty: true })}
            error={formState.errors.description?.message}
          />

          <RichTextField
            label={t('content.vacancies.fields.requirements')}
            value={values.requirements}
            onChange={(next) => setValue('requirements', next, { shouldDirty: true })}
          />

          {/* §14 renders this as a checklist, which is what a candidate reads
              down before applying — so it is a list rather than prose. */}
          <TranslatableListField
            label={t('content.vacancies.fields.documents')}
            hint={t('content.vacancies.fields.documentsHint')}
            value={values.required_documents}
            onChange={(next) => setValue('required_documents', next, { shouldDirty: true })}
          />

          <RichTextField
            label={t('content.vacancies.fields.instructions')}
            value={values.application_instructions}
            onChange={(next) => setValue('application_instructions', next, { shouldDirty: true })}
          />

          {/*
            §4 — the results, published against this vacancy. The server refuses
            the flag without a document, because §14 turns it into a link and a
            dead one lands in front of the one audience guaranteed to click.
          */}
          <fieldset className="space-y-4 rounded-card border border-subtle p-4">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('content.vacancies.results')}
            </legend>

            <p className="text-caption text-muted">{t('content.vacancies.resultsHint')}</p>

            <Input
              {...register('result_document_id')}
              type="number"
              min={1}
              label={t('content.vacancies.fields.resultDocument')}
              hint={t('content.vacancies.fields.resultDocumentHint')}
              error={formState.errors.result_document_id?.message}
            />

            <label className="flex items-center gap-2 text-body-sm text-primary">
              <input
                type="checkbox"
                {...register('is_result_published')}
                className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              />
              {t('content.vacancies.fields.resultsPublished')}
            </label>
          </fieldset>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="vacancies"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('content.fields.title'), value: values.title },
              description: { label: t('content.fields.description'), value: values.description },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    title: {},
    slug: {},
    unit: {},
    description: {},
    requirements: {},
    required_documents: {},
    application_instructions: {},
    type: 'academic',
    faculty_id: '',
    deadline: '',
    result_document_id: '',
    is_result_published: false,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: VacancyRow): Fields {
  return {
    title: row.title,
    slug: row.slug,
    unit: row.unit,
    description: row.description,
    requirements: row.requirements,
    required_documents: row.required_documents,
    application_instructions: row.application_instructions,
    type: row.type,
    faculty_id: row.faculty_id === null ? '' : String(row.faculty_id),
    deadline: row.deadline,
    result_document_id: row.result_document_id === null ? '' : String(row.result_document_id),
    is_result_published: row.is_result_published,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): VacancyInput {
  return {
    title: form.title,
    slug: form.slug,
    unit: form.unit,
    description: form.description,
    requirements: form.requirements,
    required_documents: listToInput(form.required_documents),
    application_instructions: form.application_instructions,
    type: form.type,
    faculty_id: form.faculty_id === '' ? null : Number(form.faculty_id),
    deadline: form.deadline,
    result_document_id: form.result_document_id === '' ? null : Number(form.result_document_id),
    is_result_published: form.is_result_published,
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
